import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t, type IntlService } from 'ember-intl';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Button from 'tio-reimagined/components/button';
import { PhArrowLeft, PhArrowRight, PhSparkle, PhInfo } from 'ember-phosphor-icons';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import WizardStepIndicator from './wizard-step-indicator';
import type { WizardStep } from './wizard-step-indicator';
import PhaseExplainer from './phase-explainer';
import CourseEditor from './course-editor';
import DocumentRequirements from './document-requirements';
import SubmissionGuard from './submission-guard';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';

type ApplicationType = 'type-two' | 'type-three' | 'type-four';

interface ApplicationWizardSignature {
  Args: {
    /** The TAS application being created/edited */
    application: TasApplicationModel;
    /** The application type (type-two, type-three, type-four) */
    applicationType: ApplicationType;
    /** The program template controlling form configuration */
    programTemplate: TasProgramTemplateModel;
  };
  Element: HTMLDivElement;
}

/**
 * ApplicationWizard - Multi-step guided TAS application wizard
 *
 * Replaces the legacy monolithic form with a step-by-step guided experience.
 * Steps vary by program type. Wraps existing upload infrastructure with
 * better UX and hard guards against empty submissions.
 *
 * @example
 * ```gts
 * <ApplicationWizard
 *   @application={{this.application}}
 *   @applicationType="type-two"
 *   @programTemplate={{this.programTemplate}}
 * />
 * ```
 */
export default class ApplicationWizard extends Component<ApplicationWizardSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked currentStep = 0;
  @tracked isSubmitting = false;

  get programName() {
    return this.args.application.tasProgramInstance?.tasProgramTemplate?.programName ?? '';
  }

  get isTypeTwo() {
    return this.args.applicationType === 'type-two';
  }

  get isTypeThree() {
    return this.args.applicationType === 'type-three';
  }

  get isTypeFour() {
    return this.args.applicationType === 'type-four';
  }

  get attachmentClassification() {
    return this.isTypeThree ? 'COURSE_APPROVAL_DOCUMENTS' : 'COMPLETION_DOCUMENTS';
  }

  get stepLabels(): string[] {
    const base = [
      this.intl.t('tuition_assistance.reimagined.wizard.step.overview'),
      this.intl.t('tuition_assistance.reimagined.wizard.step.courses'),
      this.isTypeThree
        ? this.intl.t('tuition_assistance.reimagined.wizard.step.pre_approval_docs')
        : this.intl.t('tuition_assistance.reimagined.wizard.step.evidence_docs'),
      this.intl.t('tuition_assistance.reimagined.wizard.step.review'),
    ];
    return base;
  }

  get steps(): WizardStep[] {
    return this.stepLabels.map((label, index) => ({
      number: index + 1,
      label,
      completed: index < this.currentStep,
      active: index === this.currentStep,
    }));
  }

  get totalSteps() {
    return this.stepLabels.length;
  }

  get isFirstStep() {
    return this.currentStep === 0;
  }

  get isLastStep() {
    return this.currentStep === this.totalSteps - 1;
  }

  get canAdvance() {
    if (this.currentStep === 1) {
      return (this.args.application.activeCourses?.length ?? 0) > 0;
    }
    return true;
  }

  get hasRequiredAttachments() {
    if (this.isTypeThree) {
      return this.args.application.hasRequiredPreApprovalAttachments ?? true;
    }
    return this.args.application.hasRequiredEvidenceAttachments ?? true;
  }

  get hasCourses() {
    return (this.args.application.activeCourses?.length ?? 0) > 0;
  }

  get submitLabel() {
    return this.isTypeThree
      ? this.intl.t('tuition_assistance.reimagined.wizard.submit.pre_approval')
      : this.intl.t('tuition_assistance.reimagined.wizard.submit.evidence');
  }

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  nextStep = () => {
    if (this.currentStep < this.totalSteps - 1 && this.canAdvance) {
      this.currentStep++;
    }
  };

  previousStep = () => {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  };

  submitApplication = async () => {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    try {
      const adapter = this.store.adapterFor('tas-application');
      if (this.isTypeThree) {
        // @ts-expect-error - Custom adapter method
        await adapter.requestCoursesApproval(this.args.application);
      } else {
        // @ts-expect-error - Custom adapter method
        await adapter.requestCourseEvidence(this.args.application);
      }
      this.router.transitionTo('authenticated.reimagined.tas.dashboard');
    } catch {
      this.isSubmitting = false;
    }
  };

  exitWizard = () => {
    this.router.transitionTo('authenticated.reimagined.tas.dashboard');
  };

  <template>
    <VStack class="gap-6" ...attributes>
      {{! Step indicator }}
      <WizardStepIndicator @steps={{this.steps}} />

      {{! Step content }}
      {{#if (this.isStep 0)}}
        {{! STEP 1: Overview }}
        <div
          class="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 text-white shadow-xl"
        >
          <div class="flex items-center gap-2 text-sm font-semibold mb-2">
            <PhSparkle class="h-4 w-4" />
            <span>{{t "tuition_assistance.reimagined.wizard.overview.badge"}}</span>
          </div>
          <Heading @level={{1}} class="text-white">
            {{t "tuition_assistance.reimagined.wizard.overview.title"}}
          </Heading>
          <Text class="text-white/80 mt-2">
            {{this.programName}}
          </Text>
        </div>

        <PhaseExplainer @state={{@application.state}} />

        {{#if this.isTypeThree}}
          <AlertBanner @intent="info">
            {{t "tuition_assistance.reimagined.wizard.overview.pre_approval_explainer"}}
          </AlertBanner>
        {{else}}
          <AlertBanner @intent="info">
            {{t "tuition_assistance.reimagined.wizard.overview.evidence_explainer"}}
          </AlertBanner>
        {{/if}}

        <Section>
          <:header>
            <div class="flex items-center gap-2">
              <PhInfo @size={{20}} class="text-indigo-500" />
              <Heading @level={{3}}>
                {{t "tuition_assistance.reimagined.wizard.overview.what_to_expect"}}
              </Heading>
            </div>
          </:header>
          <:body>
            <ol class="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_1"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_2"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_3"}}</li>
              <li>{{t "tuition_assistance.reimagined.wizard.overview.step_4"}}</li>
            </ol>
          </:body>
        </Section>
      {{/if}}

      {{#if (this.isStep 1)}}
        {{! STEP 2: Courses }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{t "tuition_assistance.reimagined.wizard.courses.title"}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.courses.subtitle"}}
            </Text>
          </:header>
          <:body>
            <CourseEditor @application={{@application}} @programTemplate={{@programTemplate}} />
          </:body>
        </Section>

        {{#unless this.hasCourses}}
          <AlertBanner @intent="warning">
            {{t "tuition_assistance.reimagined.wizard.courses.required_message"}}
          </AlertBanner>
        {{/unless}}
      {{/if}}

      {{#if (this.isStep 2)}}
        {{! STEP 3: Documents }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{#if this.isTypeThree}}
                {{t "tuition_assistance.reimagined.wizard.documents.pre_approval_title"}}
              {{else}}
                {{t "tuition_assistance.reimagined.wizard.documents.evidence_title"}}
              {{/if}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.documents.subtitle"}}
            </Text>
          </:header>
          <:body>
            <DocumentRequirements
              @programTemplate={{@programTemplate}}
              @classification={{this.attachmentClassification}}
              @uploadedCount={{this.uploadedCount}}
            />

            <div class="mt-6">
              <UploadAttachments
                @programTemplate={{@programTemplate}}
                @classification={{this.attachmentClassification}}
                @attachable={{@application}}
                @attachableType="tasApplication"
                @useDropzone={{true}}
                @uploadRequired={{true}}
                @hideDiscarded={{true}}
              />
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if (this.isStep 3)}}
        {{! STEP 4: Review & Submit }}
        <Section>
          <:header>
            <Heading @level={{2}}>
              {{t "tuition_assistance.reimagined.wizard.review.title"}}
            </Heading>
            <Text @variant="muted">
              {{t "tuition_assistance.reimagined.wizard.review.subtitle"}}
            </Text>
          </:header>
          <:body>
            <div class="space-y-4">
              {{! Course summary }}
              <div class="rounded-xl border border-gray-200 bg-white p-5">
                <Text class="font-semibold text-gray-900 mb-2">
                  {{t "tuition_assistance.reimagined.wizard.review.courses_summary"}}
                </Text>
                {{#each @application.activeCourses as |course|}}
                  <div
                    class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <Text class="text-sm">{{course.displayName}}</Text>
                    <Text class="text-sm font-medium">
                      {{this.formatCurrency course.courseTotal}}
                    </Text>
                  </div>
                {{/each}}
              </div>

              {{! Document summary }}
              <div class="rounded-xl border border-gray-200 bg-white p-5">
                <Text class="font-semibold text-gray-900 mb-2">
                  {{t "tuition_assistance.reimagined.wizard.review.documents_summary"}}
                </Text>
                <Text @variant="muted" class="text-sm">
                  {{t
                    "tuition_assistance.reimagined.wizard.review.documents_count"
                    count=this.uploadedCount
                  }}
                </Text>
              </div>

              {{! Submission guard }}
              <SubmissionGuard
                @hasRequiredAttachments={{this.hasRequiredAttachments}}
                @hasCourses={{this.hasCourses}}
                @onSubmit={{this.submitApplication}}
                @isSubmitting={{this.isSubmitting}}
                @submitLabel={{this.submitLabel}}
              />
            </div>
          </:body>
        </Section>
      {{/if}}

      {{! Navigation buttons }}
      <div class="flex items-center justify-between pt-4 border-t border-gray-200">
        {{#if this.isFirstStep}}
          <Button @appearance="outlined" {{on "click" this.exitWizard}}>
            <PhArrowLeft @size={{16}} class="mr-1" />
            {{t "tuition_assistance.reimagined.wizard.nav.back_to_dashboard"}}
          </Button>
        {{else}}
          <Button @appearance="outlined" {{on "click" this.previousStep}}>
            <PhArrowLeft @size={{16}} class="mr-1" />
            {{t "tuition_assistance.reimagined.wizard.nav.previous"}}
          </Button>
        {{/if}}

        {{#unless this.isLastStep}}
          <Button
            @intent="primary"
            disabled={{if this.canAdvance false true}}
            {{on "click" this.nextStep}}
          >
            {{t "tuition_assistance.reimagined.wizard.nav.next"}}
            <PhArrowRight @size={{16}} class="ml-1" />
          </Button>
        {{/unless}}
      </div>
    </VStack>
  </template>

  isStep = (step: number) => {
    return this.currentStep === step;
  };

  get uploadedCount() {
    if (this.isTypeThree) {
      return this.args.application.applicationApprovalAssets?.length ?? 0;
    }
    return this.args.application.evidenceAssets?.length ?? 0;
  }
}
